import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { resolveMedia } from "@/lib/api";

/**
 * Comprehensive SEO: title/description, OG, Twitter Cards, canonical,
 * verification, keywords, and JSON-LD structured data
 * (LocalBusiness + Products for each sauna model + BreadcrumbList).
 */
export default function Seo() {
  const { content } = useContent();
  const { pathname } = useLocation();
  if (!content) return null;

  const global = content.seoGlobal || {};
  const per = (content.seo || {})[pathname] || (content.seo || {})["/"] || {};
  const site = content.site || {};
  const projects = content.projects || [];

  const siteUrl = (global.siteUrl || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  const url = `${siteUrl}${pathname}`;
  const title = per.title || global.defaultTitle || global.siteName || "";
  const description = per.description || global.defaultDescription || "";
  const ogImg = resolveMedia(per.ogImage || global.defaultOgImage || "");
  const siteName = global.siteName || site.brand || "";
  const locale = global.locale || "lt_LT";
  const robots = global.robots || "index,follow";

  // ---- JSON-LD ----
  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${siteUrl}/#business`,
    name: siteName,
    description: global.defaultDescription || site.philosophyShort || "",
    url: siteUrl,
    telephone: site.phoneHref || site.phone,
    email: site.email,
    image: ogImg || undefined,
    address: {
      "@type": "PostalAddress",
      addressLocality: site.city || "Klaipėda",
      addressCountry: "LT",
    },
    areaServed: { "@type": "Country", name: "Lietuva" },
    sameAs: [site.instagram, site.facebook].filter(Boolean),
  };

  const products = projects
    .filter((p) => p.name && p.priceFrom)
    .map((p) => ({
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": `${siteUrl}/projektai#${p.slug}`,
      name: p.name,
      description: p.intro || p.tagline,
      image: p.cover ? resolveMedia(p.cover) : undefined,
      brand: { "@type": "Brand", name: siteName },
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        priceSpecification: {
          "@type": "PriceSpecification",
          priceCurrency: "EUR",
          price: (p.priceFrom || "").replace(/[^\d]/g, ""),
        },
        availability: "https://schema.org/PreOrder",
        url: `${siteUrl}/projektai#${p.slug}`,
      },
    }));

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Pagrindinis", item: siteUrl + "/" },
      pathname !== "/" && {
        "@type": "ListItem",
        position: 2,
        name: per.title || pathname,
        item: url,
      },
    ].filter(Boolean),
  };

  return (
    <Helmet>
      <html lang="lt" />
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {global.keywords && <meta name="keywords" content={global.keywords} />}
      <meta name="robots" content={robots} />
      <meta name="theme-color" content={global.themeColor || "#1a1a1a"} />
      {global.geoRegion && <meta name="geo.region" content={global.geoRegion} />}
      {global.geoPlacename && <meta name="geo.placename" content={global.geoPlacename} />}
      <meta name="author" content={siteName} />
      {global.googleVerification && (
        <meta name="google-site-verification" content={global.googleVerification} />
      )}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={locale} />
      <meta property="og:url" content={url} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {ogImg && <meta property="og:image" content={ogImg} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content={ogImg ? "summary_large_image" : "summary"} />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {ogImg && <meta name="twitter:image" content={ogImg} />}
      {global.twitterHandle && <meta name="twitter:site" content={global.twitterHandle} />}

      {/* JSON-LD structured data */}
      <script type="application/ld+json">{JSON.stringify(localBusiness)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
      {products.map((p, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(p)}</script>
      ))}
    </Helmet>
  );
}
